package diana.soleil.design.iraniancanadienbazzar.core.designsystem.component

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.style.TextOverflow
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme

/**
 * Compact business row for lists like Home "Popular Near You" (Stitch fac255ec): thumbnail, name,
 * category, rating + distance, and a favorite toggle. [thumbnail] slot draws the logo/photo.
 * [newLabel] (localized "New", passed by the caller) replaces the star rating for listings with no
 * reviews yet — a fresh directory must never read as a wall of "0.0" businesses.
 */
@Composable
fun BusinessListItem(
    name: String,
    category: String,
    rating: Float,
    distance: String,
    modifier: Modifier = Modifier,
    newLabel: String? = null,
    favorite: Boolean = false,
    onFavoriteClick: (() -> Unit)? = null,
    favoriteContentDescription: String? = null,
    onClick: (() -> Unit)? = null,
    coverIcon: String = "storefront",
    coverImageRes: org.jetbrains.compose.resources.DrawableResource? = null,
    thumbnail: @Composable (() -> Unit)? = null,
) {
    AppCard(modifier = modifier.fillMaxWidth(), onClick = onClick) {
        Row(
            modifier = Modifier.padding(AppTheme.spacing.md),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
        ) {
            Box(
                modifier = Modifier
                    .size(AppTheme.sizes.listThumbnail)
                    .clip(AppTheme.shapes.md),
            ) {
                if (thumbnail != null) thumbnail() else BusinessCover(iconName = coverIcon, placeholderImageRes = coverImageRes)
            }
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = name,
                    style = AppTheme.typography.headlineMd,
                    color = AppTheme.colors.onSurface,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
                Text(
                    text = category,
                    style = AppTheme.typography.bodySm,
                    color = AppTheme.colors.onSurfaceVariant,
                    modifier = Modifier.padding(top = AppTheme.spacing.xxs),
                )
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.xxs),
                    modifier = Modifier.padding(top = AppTheme.spacing.xs),
                ) {
                    if (newLabel != null) {
                        AccentBadge(label = newLabel)
                    } else {
                        AppIcon(name = "star", size = AppTheme.sizes.iconSm, tint = AppTheme.colors.secondary, filled = true)
                        Text(
                            text = ratingLabel(rating),
                            style = AppTheme.typography.bodySm,
                            color = AppTheme.colors.onSurface,
                        )
                    }
                    if (distance.isNotBlank()) {
                        Text(
                            text = "·",
                            style = AppTheme.typography.bodySm,
                            color = AppTheme.colors.onSurfaceVariant,
                        )
                        Text(
                            text = distance,
                            style = AppTheme.typography.bodySm,
                            color = AppTheme.colors.onSurfaceVariant,
                        )
                    }
                }
            }
            AppIcon(
                name = "favorite",
                size = AppTheme.sizes.iconMd,
                tint = if (favorite) AppTheme.colors.primary else AppTheme.colors.onSurfaceVariant,
                filled = favorite,
                contentDescription = favoriteContentDescription,
                modifier = if (onFavoriteClick != null) Modifier.clickable(onClick = onFavoriteClick) else Modifier,
            )
        }
    }
}
